import React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { ScrollView, StyleSheet, View } from "react-native";
import moment from "moment";

import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import {
  PdHookFormDateRangeInput,
  PdHookFormErrorMessage,
  PdHookFormInput,
  PdHookFormInputNumber,
  PdHookFormInputPercentage,
  PdHookFormSwitch,
} from "src/components/hook-form";
import { PdStack, PdText } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { tModalComponentProps } from "src/redux/modals/types";
import { getApiError, postCartAbandonedRecovery } from "src/util/apiNew";
import { useAppDispatch } from "src/redux";

import { useIsMobile } from "../ui";
import { formatMoney, formatPercentage } from "src/util/gFormatting";

type tModalParams = {
  cartIds: string[];
  brandName: string;
  contactFirstName: string;
};

type tMessageRetailersFormData = {
  includePromotion: boolean;
  message: string;
  promotionCode: string;
  promotionTitle: string;
  promotionDescription: string;
  promotionPeriod: [Date, Date];
  promotionMinimumOrder: number | null;
  promotionIsPercentDiscount: boolean;
  promotionFixedDiscount: number | null;
  promotionPercentDiscount: number | null;
};

const formatDate = (dateInMilliseconds: number) => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const DEFAULT_MESSAGE = `Hi there,

We noticed that (brandName) products are waiting in your cart. We take great pride in our brand and products and would love to answer any questions you might have.

Thank you for your support,
(name)`;

const DEFAULT_PROMO_MESSAGE = `Hi there,

We noticed that (brandName) products are waiting in your cart. Please enjoy this special promotion we put together for you and don't hesitate to reach out to us if you have any questions about our brand and products.

Promo details

Use code (promotionCode) at checkout for (promotionDiscount) off, from (promotionPeriod). Minimum spend is (promotionMinimumOrder).

Thank you for your support,
(name)`;

const SendAbandonedCartPromotionEmail: React.FC<tModalComponentProps<tModalParams>> = ({
  onResolve,
  onClose,
  cartIds,
  brandName,
  contactFirstName,
}) => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const formMethods = useForm<tMessageRetailersFormData>({
    defaultValues: {
      includePromotion: false,
      message: DEFAULT_MESSAGE.replace("(brandName)", brandName).replace("(name)", contactFirstName),
      promotionCode: "",
      promotionTitle: "",
      promotionDescription: "",
      promotionPeriod: [moment().toDate(), moment().add(1, "month").toDate()],
      promotionMinimumOrder: null,
      promotionIsPercentDiscount: false,
      promotionFixedDiscount: null,
      promotionPercentDiscount: null,
    },
  });
  const [
    includePromotion,
    promotionCode,
    promotionPeriod,
    promotionMinimumOrder,
    promotionIsPercentDiscount,
    promotionFixedDiscount,
    promotionPercentDiscount,
  ] = useWatch({
    name: [
      "includePromotion",
      "promotionCode",
      "promotionPeriod",
      "promotionMinimumOrder",
      "promotionIsPercentDiscount",
      "promotionFixedDiscount",
      "promotionPercentDiscount",
    ],
    control: formMethods.control,
  });

  const { setValue } = formMethods;

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSubmit = formMethods.handleSubmit(async (values) => {
    try {
      await postCartAbandonedRecovery({
        cartIds,
        message: values.message,
        promotion: values.includePromotion
          ? {
              code: values.promotionCode,
              title: values.promotionTitle,
              description: values.promotionDescription,
              startDateInMilliseconds: moment(values.promotionPeriod[0]).startOf("day").valueOf(),
              endDateInMilliseconds: moment(values.promotionPeriod[1]).startOf("day").valueOf(),
              minimumOrder: values.promotionMinimumOrder || 0,
              fixedDiscount: values.promotionFixedDiscount || undefined,
              percentDiscount: values.promotionPercentDiscount ? values.promotionPercentDiscount / 100 : undefined,
            }
          : undefined,
      });
      dispatch({ type: "RIBBON", message: "E-mail sent" });
      onResolve();
    } catch (error) {
      const apiError = getApiError(error);
      dispatch({ type: "RIBBON", message: new Error(apiError.message) });
    }
  });

  React.useEffect(() => {
    setValue(
      "message",
      includePromotion
        ? DEFAULT_PROMO_MESSAGE.replace("(brandName)", brandName)
            .replace("(promotionCode)", promotionCode || "-")
            .replace(
              "(promotionDiscount)",
              promotionIsPercentDiscount
                ? formatPercentage((promotionPercentDiscount || 0) / 100)
                : formatMoney(promotionFixedDiscount || 0),
            )
            .replace(
              "(promotionPeriod)",
              `${formatDate(moment(promotionPeriod[0]).startOf("day").valueOf())} to ${formatDate(
                moment(promotionPeriod[1]).startOf("day").valueOf(),
              )}`,
            )
            .replace("(promotionMinimumOrder)", formatMoney(promotionMinimumOrder || 0))
            .replace("(name)", contactFirstName)
        : DEFAULT_MESSAGE.replace("(brandName)", brandName).replace("(name)", contactFirstName),
    );
  }, [
    brandName,
    contactFirstName,
    includePromotion,
    promotionCode,
    promotionFixedDiscount,
    promotionIsPercentDiscount,
    promotionMinimumOrder,
    promotionPercentDiscount,
    promotionPeriod,
    setValue,
  ]);

  return (
    <PdModal style={styles.modal}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <PdModalTitle style={styles.mb5}>Message Retailers with Abandoned Carts!</PdModalTitle>
        <PdText style={styles.mb5}>
          We&apos;ll send an email on your behalf to all retailers that have an abandoned cart for your brand.
        </PdText>

        <FormProvider {...formMethods}>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb3]}>
            <PdHookFormSwitch name="includePromotion" style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }} />
            <PdText style={styles.ml2}>Include promotion?</PdText>
          </View>

          {includePromotion && (
            <>
              <PdStack
                style={[styles.widthFull, styles.mb3]}
                direction={isMobile ? "vertical" : "horizontal"}
                layout={[1, 1]}
                gap={3}
              >
                <View>
                  <PdFormLabel>Promotion Code *</PdFormLabel>
                  <PdHookFormInput
                    name="promotionCode"
                    placeholder="PROMO1"
                    isSpacesAllowed={false}
                    rules={{ required: "Required" }}
                  />
                  <PdHookFormErrorMessage name="promotionCode" />
                </View>
                <View>
                  <PdFormLabel>Promotion Title *</PdFormLabel>
                  <PdHookFormInput
                    name="promotionTitle"
                    placeholder="ex: Holiday 2023!"
                    rules={{ required: "Required" }}
                  />
                  <PdHookFormErrorMessage name="promotionTitle" />
                </View>
              </PdStack>

              <View style={[styles.widthFull, styles.mb3]}>
                <PdFormLabel>Promotion Description *</PdFormLabel>
                <PdHookFormInput
                  name="promotionDescription"
                  placeholder="ex: Purchase must include a case of Vanilla candles"
                  rules={{ required: "Required" }}
                />
                <PdHookFormErrorMessage name="promotionDescription" />
              </View>

              <PdStack
                style={[styles.widthFull, styles.zIndex1, styles.mb3]}
                direction={isMobile ? "vertical" : "horizontal"}
                layout={[1, 1]}
                gap={3}
              >
                <View style={styles.zIndex1}>
                  <PdFormLabel>Period *</PdFormLabel>
                  <PdHookFormDateRangeInput name="promotionPeriod" rules={{ required: "Required" }} />
                  <PdHookFormErrorMessage name="promotionPeriod" />
                </View>
                <View>
                  <PdFormLabel>Minimum Order *</PdFormLabel>
                  <View style={styles.pdInputGroup}>
                    <PdText style={styles.ml2}>$</PdText>
                    <PdHookFormInputNumber
                      name="promotionMinimumOrder"
                      placeholder="0"
                      style={[styles.widthFull, styles.border0]}
                      rules={{ required: "Required" }}
                    />
                  </View>
                  <PdHookFormErrorMessage name="promotionMinimumOrder" />
                </View>
              </PdStack>

              <PdStack style={[styles.widthFull, styles.mb3]} direction={isMobile ? "vertical" : "horizontal"} gap={3}>
                <View>
                  <PdFormLabel>Prefer applying a percent discount?</PdFormLabel>
                  <View style={[styles.flexRow, styles.alignCenter, styles.inputHeight]}>
                    <PdHookFormSwitch
                      name="promotionIsPercentDiscount"
                      style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }}
                      onValueChange={() => {
                        formMethods.setValue("promotionFixedDiscount", null);
                        formMethods.setValue("promotionPercentDiscount", null);
                      }}
                    />
                    <PdText style={styles.ml2}>{!promotionIsPercentDiscount ? "Yes" : "No"}</PdText>
                  </View>
                </View>
                {promotionIsPercentDiscount ? (
                  <View style={styles.flex1}>
                    <PdFormLabel>Percent Discount</PdFormLabel>
                    <View style={styles.pdInputGroup}>
                      <PdHookFormInputPercentage
                        name="promotionPercentDiscount"
                        placeholder="0"
                        style={[styles.widthFull, styles.border0]}
                        rules={{ required: "Required" }}
                      />
                      <PdText style={styles.mr2}>%</PdText>
                    </View>
                    <PdHookFormErrorMessage name="promotionPercentDiscount" />
                  </View>
                ) : (
                  <View style={styles.flex1}>
                    <PdFormLabel>Fixed Discount</PdFormLabel>
                    <View style={styles.pdInputGroup}>
                      <PdText style={styles.ml2}>$</PdText>
                      <PdHookFormInputNumber
                        name="promotionFixedDiscount"
                        placeholder="0"
                        style={[styles.widthFull, styles.border0]}
                        rules={{ required: "Required" }}
                      />
                    </View>
                    <PdHookFormErrorMessage name="promotionFixedDiscount" />
                  </View>
                )}
              </PdStack>
            </>
          )}

          <View style={[styles.widthFull, styles.mb6]}>
            <PdFormLabel>Message *</PdFormLabel>
            <PdHookFormInput
              name="message"
              placeholder="Message"
              multiline={true}
              numberOfLines={8}
              rules={{ required: "Required" }}
            />
            <PdHookFormErrorMessage name="message" />
          </View>

          <PdButton style={styles.mlAuto} onPress={handleOnSubmit}>
            <PdButtonLabel>{includePromotion ? "Create promotion and send e-mail" : "Send e-email"}</PdButtonLabel>
          </PdButton>
        </FormProvider>
      </ScrollView>
    </PdModal>
  );
};

const useSendAbandonedCartPromotionEmail = (): tUseModal<tModalParams> => {
  return useModal<tModalParams>(SendAbandonedCartPromotionEmail);
};

export default useSendAbandonedCartPromotionEmail;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  border0: {
    borderWidth: 0,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  inputHeight: {
    height: 37,
  },
  mb3: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  mb5: {
    marginBottom: kStyleConst.kSpacing * 5,
  },
  mb6: {
    marginBottom: kStyleConst.kSpacing * 6,
  },
  ml2: {
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  widthFull: {
    width: "100%",
  },
  zIndex1: {
    zIndex: 1,
  },
});
